import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ActivityItem from "./ActivityItem";
import { ACTION_MAPPING_CONFIG, ActionDescriptionFn } from "./constants";
import styles from "./styles.module.scss";
import { getActionsHistory } from "../../../setup/api/services/contractActions";
import {
  ApiActivity,
  ActivityMapped,
} from "../../../setup/api/services/contractActions/models";
import { useOrganizationCode } from "../../../setup/util/extractOrganizationCode";
import { AuthConfig } from "../../../setup/context/config/authConfig";
import Pagination from "../pagination";
import { ROUTES } from "../../../setup/constants";
import { useNavigate } from "react-router-dom";

const ACTOR_ROLE_ARABIC: Record<string, string> = {
  SENDER: "المرسل",
  RECEIVER: "المستلم",
  PROVIDER: "المزود",
  CUSTOMER: "العميل",
  BROKER: "الوسيط",
  ADMIN: "المشرف",
  ARBITRATOR: "المحكّم",
  SYSTEM: "النظام",
};

const getActivityMapper = (action: string) => {
  const config =
    ACTION_MAPPING_CONFIG[action as keyof typeof ACTION_MAPPING_CONFIG];

  return (item: ApiActivity): ActivityMapped => {
    // Determine actor display
    const actorDisplay =
      item?.actorRole === "ADMIN" ||
      (item?.actorRole === "SYSTEM" &&
        (item.action === "CASH_OUT" ||
          item.action === "CASH_OUT_CONFIRMED" ||
          item.action === "MONEY_BACKED_TO_USER_CONFIRMED"))
        ? "فريق وفّي"
        : item?.actor?.profile?.fullName ||
          ACTOR_ROLE_ARABIC[item.actorRole] ||
          item?.actorId ||
          "مجهول";

    // Title
    const actionTitle =
      typeof config?.title === "function"
        ? config.title(item.amount)
        : config?.title || item.action.replace(/_/g, " ");

    // Description
    let actionDescription = `${actorDisplay} قام بتنفيذ إجراء`;

    if (config?.description) {
      const descFunc = config.description as ActionDescriptionFn;

      // Decide how to call description function
      if (
        descFunc.length === 3 &&
        item.amount !== undefined &&
        item.transactionParties
      ) {
        actionDescription = (
          descFunc as (
            actorName: string,
            amount: number,
            targetId: string
          ) => string
        )(
          actorDisplay,
          item.amount,
          item.transactionParties.beneficiary.profile?.fullName || ""
        );
      } else if (descFunc.length === 2 && item.amount !== undefined) {
        actionDescription = (
          descFunc as (actorName: string, amount: number) => string
        )(actorDisplay, item.amount);
      } else {
        actionDescription = (descFunc as (actorName: string) => string)(
          actorDisplay
        );
      }
    }

    return {
      id: crypto.randomUUID(),
      actionTitle,
      actionDescription,
      actor: actorDisplay,
      amount: item.amount,
      timestamp: item.createdAt,
      unread: false, // TODO: for now
      contractName: item.contractTitle ? `معامله : ${item.contractTitle}` : "",
      contractId: item?.contractId,
    };
  };
};

// Map API activities → UI-friendly activities
export const mapActivities = (apiActivities: ApiActivity[]): ActivityMapped[] =>
  apiActivities.map((item) => getActivityMapper(item.action)(item));

const BusinessUpdates = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userToken = AuthConfig.getToken();
  const authorities = AuthConfig.getUserInfo()?.authorities;
  const organizationCode = useOrganizationCode(userToken) || undefined;

  const [pagesCount, setPagesCount] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const pageSize = 10;

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [activities, setActivities] = useState<ActivityMapped[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchOrganizationHistory = async () => {
      if (!organizationCode) return;
      setIsLoading(true);
      try {
        const response = await getActionsHistory({
          organizationCode,
          page: currentPage - 1,
          size: pageSize,
        });
        setPagesCount(response.data.totalPages);
        setTotalCount(response.data.totalElements);

        setActivities(mapActivities(response?.data?.content));
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrganizationHistory();
  }, [activities?.length, currentPage, organizationCode]);

  const handleReadNotification = (index: number) => {
    setActivities((prevActivities) =>
      prevActivities.map((activity, i) => ({
        ...activity,
        unread: i === index ? false : activity.unread,
      }))
    );
    if (
      authorities?.includes("CLIENT_ADMIN_VIEW_CONTRACT") ||
      authorities?.includes("CLIENT_ADMIN") ||
      authorities?.includes("ORGANIZATION_OWNER")
    )
      navigate(
        ROUTES.CONTRACT_DETAILS.replace(
          ":id",
          activities?.[index]?.contractId || ""
        )
      );
    // TODO: API to mark as read
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        height: "100%",
        paddingTop: "48px",
      }}>
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>{t("lbl_latest_updates")}</h1>
      </div>
      <div className={styles.updatesFeedContainer}>
        {activities.map((activity, index) => (
          <ActivityItem
            key={activity.id}
            item={activity}
            onClick={() => handleReadNotification(index)}
          />
        ))}
      </div>
      <div
        className={
          activities?.length
            ? styles.updatesContainer
            : styles.updatesContainerHide
        }>
        <Pagination
          countName={t("lbl_update")}
          pagesCount={pagesCount}
          length={totalCount}
          page={currentPage}
          pageSize={pageSize}
          handlePaginationChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default BusinessUpdates;
